import { Vector3 } from "three";
import { Previewable, PreviewManager } from "../object/PreviewManager";
import {
  InputPointerType,
  InputEventType,
  InputMultiPointerEvent,
  ISwipeArgs,
  IPinchArgs,
} from "../utils/input-handler/interface";
import { Viewer } from "../viewer";
import * as Config from "../config/ViewerConfig";
import { IMeasurementPreviewCreation } from "./types";
import { generatePreviewMeasurmentId } from "./utils";
export class TwoPointMeasurementCreationPreview
  implements IMeasurementPreviewCreation
{
  points: Vector3[];
  private _viewer: Viewer;
  private _preview: PreviewManager;
  private _pinId1: number;
  private _pinId2: number;
  private _lineId1: number;

  private _isPointPickedOnStartDrag = false;

  private _resolve: any;
  private _reject: any;

  private _id: number;

  public get id() {
    return this._id;
  }

  constructor(viewer: Viewer, preview: PreviewManager) {
    this._viewer = viewer;
    this._preview = preview;
    this.points = [];
    this._id = generatePreviewMeasurmentId.next().value as number;
  }

  public startMeasurement(): Promise<Vector3[]> {
    return new Promise((resolve, reject) => {
      this._resolve = resolve;
      this._reject = reject;
      const previewables = this._preview.init([
        {
          type: Previewable.PIN,
          count: 2,
          color: Config.PREVIEW_MEASUREMENT_COLOR,
        },
        {
          type: Previewable.THICK_LINE,
          count: 1,
          color: Config.PREVIEW_MEASUREMENT_COLOR,
        },
      ]);
      this._pinId1 = previewables[0].ids[0];
      this._pinId2 = previewables[0].ids[1];
      this._lineId1 = previewables[1].ids[0];
    });
  }

  public finishMeasurement(status = true, message = ""): void {
    if (status && this.points.length === 2) {
      this._resolve([...this.points]);
    } else {
      this._reject({ message, id: this.id });
    }
    this.dispose();
  }

  public dispose() {
    this.points.length = 0;
    this._preview.dispose([this._pinId1, this._pinId2, this._lineId1]);
    const event = new CustomEvent("onPointClick", {
      detail: { count: this.points.length },
    });
    document.dispatchEvent(event);
  }

  private updatePosition(point: Vector3) {
    //updates for the latest one
    const pinId = this.points.length === 0 ? this._pinId1 : this._pinId2;
    this._preview.updatePreview(pinId, {
      position: point,
      type: Previewable.PIN,
      visible: true,
    });
    if (this.points.length === 1) {
      this._preview.updatePreview(this._lineId1, {
        type: Previewable.LINE,
        point1: this.points[0],
        point2: point,
        visible: true,
      });
    }
  }

  public handlePointerEvent(
    pointer: { pointerType: InputPointerType; eventType: InputEventType },
    event: PointerEvent
  ): void {
    switch (pointer.eventType) {
      case InputEventType.POINTER_HOVER: //touch cannot hover
        const point = this._viewer.intersectPointCloud(event);
        point && this.updatePosition(point);
        break;
      case InputEventType.CLICK: {
        //if possible click will resolve
        const currentPinId =
          this.points.length === 0 ? this._pinId1 : this._pinId2;
        const pin = this._preview.getPin(currentPinId);
        if (!pin.visible) return;
        if (event.pointerType === "touch") {
          //shift the point downward
          let { clientX, clientY } = event;
          clientY += Config.TOUCH_OFFSET;
          const point = this._viewer.intersectPointCloud({ clientX, clientY });
          point && this.updatePosition(point);
        }
        if (pointer.pointerType === InputPointerType.PRIMARY) {
          const point = this._preview.getPinPosition(currentPinId);

          if (point) {
            this.points.push(point.clone());
            const event = new CustomEvent("onPointClick", {
              detail: { count: this.points.length },
            });
            document.dispatchEvent(event);
            this._viewer.setPointsToMeasurement(undefined, this.points);
            if (this.points.length === 2) {
              this.finishMeasurement();
            }
          }
        }
        break;
      }
      case InputEventType.START_DRAG: //Start drag cannot resolve, end drag can resolve
        let { clientX, clientY } = event;
        if (event.pointerType === "touch") {
          //shift the point downward
          clientY += Config.TOUCH_OFFSET;

          if (pointer.pointerType === InputPointerType.PRIMARY) {
            const point = this._viewer.intersectPointCloud({
              clientX,
              clientY,
            });
            if (point) {
              this._viewer.enableControl({ rotate: false });
              this.updatePosition(point);
              this._isPointPickedOnStartDrag = true;
            }
            // if (point) {
            //   this.points.push(point.clone());
            // }
          }
        }

        break;
      case InputEventType.DRAGGED: {
        // end drag is required to resolve
        let { clientX, clientY } = event;

        if (event.pointerType === "touch") {
          //shift the point downward
          if (this._isPointPickedOnStartDrag) {
            clientY += Config.TOUCH_OFFSET;
            if (pointer.pointerType === InputPointerType.PRIMARY) {
              const point = this._viewer.intersectPointCloud({
                clientX,
                clientY,
              });
              point && this.updatePosition(point);
            }
          }
        }

        break;
      }
      case InputEventType.END_DRAG: {
        if (pointer.pointerType === InputPointerType.PRIMARY) {
          this._viewer.enableControl({ pan: true, rotate: true });
          if (event.pointerType === "touch") {
            if (this._isPointPickedOnStartDrag) {
              this._isPointPickedOnStartDrag = false;

              const point = this._preview.getPinPosition(
                this.points.length === 0 ? this._pinId1 : this._pinId2
              );

              if (point) {
                this.points.push(point.clone());
                const event = new CustomEvent("onPointClick", {
                  detail: { count: this.points.length },
                });
                document.dispatchEvent(event);
                this._viewer.setPointsToMeasurement(undefined, this.points);
                if (this.points.length === 2) {
                  this.finishMeasurement();
                }
              }
            }
          }
        }
        break;
      }
    }
  }

  public handleMultiplePointerEvent(
    eventType: InputMultiPointerEvent,
    args: ISwipeArgs | IPinchArgs
  ): void {
    console.warn("Multiple Pointer not implemented");
    // throw new Error("Method not implemented.");
  }

  public setPoints(points: Vector3[]): void {
    this.points = points.map((p) => p.clone());

    if (this.points.length === 0) {
      this._preview.updatePreview(this._pinId1, {
        type: Previewable.PIN,
        visible: false,
      });
      this._preview.updatePreview(this._pinId2, {
        type: Previewable.PIN,
        visible: false,
      });
      this._preview.updatePreview(this._lineId1, {
        type: Previewable.LINE,
        visible: false,
      });
    } else if (this.points.length === 1) {
      this._preview.updatePreview(this._pinId2, {
        type: Previewable.PIN,
        visible: false,
      });
      this._preview.updatePreview(this._pinId1, {
        position: this.points[0],
        type: Previewable.PIN,
        visible: true,
      });
    }
    const event = new CustomEvent("onPointClick", {
      detail: { count: this.points.length },
    });
    document.dispatchEvent(event);
    this._viewer.updateRenderer();
  }
}
